import * as React from "react";
import { observer } from "mobx-react";

import { autorun } from "mobx";
import EsriGraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import EsriGraphic from "@arcgis/core/Graphic";
import {
    FillSymbolConfiguration,
    SimpleLineSymbolConfiguration,
    SimpleMarkerSymbolConfiguration
} from "../../core/config/symbol-configuration";
import { AppState } from "../../stores/app-mobx";
import { UiState } from "../../core/ui-state";
import ViewClickEvent = __esri.ViewClickEvent;
import { withRouter } from "../withRouter";
import * as identify from "@arcgis/core/rest/identify";
import IdentifyParameters from "@arcgis/core/rest/support/IdentifyParameters";
import { Constants } from "../../globals/constants";
import Viewpoint from "@arcgis/core/Viewpoint";

export interface ProjectSelectionProps {
    view: __esri.MapView | __esri.SceneView | undefined;
    uiStore: UiState;
    appStore: AppState;
    navigate: any;
}

/**
 * Custom component as a visual container for all Webis API functions
 * Contains a mode switcher and a coordinate picker
 */
@observer
class ProjectSelection extends React.Component<ProjectSelectionProps, any> {
    widgetKey = "webis_api";
    highlightLayerTitle = "webis_highlight_layer";
    autorunDisposers: any[] = [];
    mapClickWatchHandle: any;
    highlightLayer: EsriGraphicsLayer;
    disabled: false;
    highlightSymbols: {
        point: SimpleMarkerSymbolConfiguration;
        polyline: SimpleLineSymbolConfiguration;
        polygon: FillSymbolConfiguration;
    };

    constructor(props: ProjectSelectionProps) {
        super(props);
        this.highlightLayer = new EsriGraphicsLayer({ listMode: "hide", title: this.highlightLayerTitle });
        this.highlightSymbols = {
            point: {
                type: "simple-marker",
                style: "circle",
                size: 15,
                outline: {
                    color: [0, 255, 255, 1],
                    width: 2.25
                }
            } as SimpleMarkerSymbolConfiguration,
            polyline: {
                style: "solid",
                color: [0, 255, 255, 1],
                width: 2.25,
                type: "simple-line"
            } as SimpleLineSymbolConfiguration,
            polygon: {
                type: "simple-fill",
                color: [0, 255, 255, 0],
                outline: {
                    color: [0, 255, 255, 1],
                    width: 2.25
                }
            } as FillSymbolConfiguration
        };
    }

    componentDidMount(): void {

        // Handle project selection in map according the state for project selecting enabled
        this.autorunDisposers.push(autorun(() => {
            const { view, appStore } = this.props;
            if (!view || !view.map || !appStore) {
                return;
            }
            if (appStore.projectSelectingEnabled) {
                // add watch for clicks on the map view
                this.addMapClickWatch(view);
            }

            // react when project selecting is enabled / disabled
            this.autorunDisposers.push(
                autorun(() => {
                    if (!appStore.projectSelectingEnabled) {
                        this.removeMapClickWatch();
                    } else {
                        this.addMapClickWatch(view);
                    }
                })
            );

        }));

        // Handle project zooming and highlighting
        this.autorunDisposers.push(autorun(() => {
            const { view } = this.props;
            const { projectId, areaId, zoomingRequired, startupExtent } = this.props.appStore;
            if (!view || !view.map) {
                return;
            }

            this.props.appStore.closeMessageBox();
            this.highlightLayer.removeAll();
            if (!view.map.allLayers.some((l) => l.title === this.highlightLayerTitle)) {
                view.map.add(this.highlightLayer);
            }

            // Query the features and add them to the highlight layer
            if (projectId || areaId) {
                view.map.layers.forEach((layer: __esri.Layer) => {
                    if (layer.hasOwnProperty("allSublayers")) {
                        (layer as any).allSublayers.forEach((l: __esri.FeatureLayer) => {
                            const query = l.createQuery();
                            query.where = this.props.appStore.getProjectAreaWhereClause();
                            l.queryFeatures(query).then((response: __esri.FeatureSet) => {
                                if (response.features.length > 0) {
                                    const symbol: any = this.highlightSymbols[response.features[0].geometry.type];
                                    response.features.forEach((graphic: __esri.Graphic) => {
                                        this.highlightLayer.add(new EsriGraphic({
                                            geometry: graphic.geometry,
                                            symbol
                                        }));
                                    });
                                    l.visible = true;
                                    if (zoomingRequired) {
                                        this.zoom();
                                    }
                                }
                                // No areas found for this project. Zoom to default map extent.
                                else {
                                    if (startupExtent?.viewpoint) {
                                        view.goTo(new Viewpoint(startupExtent.viewpoint as __esri.ViewpointProperties));
                                    }
                                }
                            }).catch(() => {
                            });
                        });
                    }
                });
            }
        }));
    }

    zoom(): void {
        const { view, uiStore } = this.props;
        if (view && this.highlightLayer) {
            if (this.highlightLayer.graphics.length > 0) {
                const scale = this.highlightLayer.graphics.at(0).geometry?.type === "point" ? 1128 : 0;
                // eslint-disable-next-line max-len
                const gotoTarget = scale === 0 ? { target: this.highlightLayer.graphics } : {
                    target: this.highlightLayer.graphics,
                    scale
                };
                view.goTo(gotoTarget).catch((error) => {
                    if (error.name !== "view:goto-interrupted") {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                });
                uiStore.PopupStore.setVisible(false);
            }
        }
    }

    private addMapClickWatch(view: __esri.MapView | __esri.SceneView): void {
        const { appStore, navigate } = this.props;
        if (this.mapClickWatchHandle) {
            // watch is already active
            return;
        }
        this.mapClickWatchHandle = view.on("click", (event) => {
            const identifyParams = new IdentifyParameters();
            identifyParams.tolerance = 3;
            identifyParams.layerOption = "all";
            identifyParams.geometry = event.mapPoint;
            identifyParams.mapExtent = view.extent;
            view.map.layers.forEach((layer: __esri.FeatureLayer) => {
                if (layer.url !== null) {
                    identify.identify(layer.url, identifyParams).then((response) => {
                        response.results.forEach((result: __esri.IdentifyResult) => {
                            if (result.layerName.includes("FFF Schaffungsflächen")
                                || result.layerName.includes("FFF Beanspruchungsflächen")) {
                                const attr = result.feature.attributes;
                                const projectType = attr["PROJECT_TYPE"];

                                if (projectType !== Constants.projectTypeRecultivation) {
                                    const projectCategory = attr["PROJECT_CATEGORY"];
                                    let projectId;

                                    if (projectCategory === Constants.projectCategoryUsage) {
                                        projectId = attr["BEANSPR_ID"];
                                    } else {
                                        projectId = attr["SCHAFFPR_ID"];
                                    }

                                    appStore.setProjectId(projectCategory, projectId, true);
                                    navigate(`projekte/${projectCategory}/${projectId}`);
                                }
                            }
                        });
                    });
                }
            });
        });
    }

    private removeMapClickWatch(): void {
        this.mapClickWatchHandle?.remove();
        this.mapClickWatchHandle = null;
    }

    componentWillUnmount(): void {
        const { uiStore, view } = this.props;
        this.removeMapClickWatch();
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        this.props.appStore.closeMessageBox();
        view?.map.remove(this.highlightLayer);
        uiStore.removeWidget(this.widgetKey);
    }

    render() {
        return null;
    }
}

export default withRouter(ProjectSelection);
